import type { AIDenoiserProcessorLevel, ConnectionState, NetworkQuality } from '@/services/agora.types';
import type { LocalTrack } from '@/services/stream.service';
import type { AgoraToken, MediaRoom, RtcDevices } from '@/services/utils/liveMedia.utils';

export type DeviceType = 'camera' | 'microphone' | 'speaker';
export type SignalMetadata = {
  networkQuality: NetworkQuality;
  strength: number;
};

export enum HDRecordingStatus {
  IDLE = 'idle',
  STARTING = 'starting',
  RETRYING = 'retrying',
  RECORDING = 'recording',
  RECORDING_WITH_SLOW_PERFORMANCE = 'recording_with_slow_performance',
  FAILED = 'failed'
}

export enum SDRecordingStatus {
  IDLE = 'idle',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  FAILED = 'failed'
}

export enum MediaTrackType {
  VIDEO = 'video',
  AUDIO = 'audio'
}

export interface MediaRoomState {
  mediaRooms: {
    [roomID: string]: MediaRoom;
  };
  agoraTokens: AgoraToken[];
  localTracks: LocalTrack[];
  screenTracks: LocalTrack[];
  localAudioLevel: number;
  signalMetadata: SignalMetadata;
  remoteSignalMetadata: {
    [uid: string]: SignalMetadata;
  };
  stageControls: {
    video: Boolean;
    audio: Boolean;
  };
  currentDevices: {
    camera: MediaDeviceInfo | null | undefined;
    microphone: MediaDeviceInfo | null | undefined;
    speaker: MediaDeviceInfo | null | undefined;
  };
  rtcDeviceAccessError: {
    camera: Boolean;
    microphone: Boolean;
    speaker: Boolean;
  };
  rtcDeviceBrowserPermission: {
    camera: Boolean;
    microphone: Boolean;
    speaker: Boolean;
  };
  rtcDeviceSystemPermission: {
    camera: Boolean;
    microphone: Boolean;
    speaker: Boolean;
  };
  rtcDevices: RtcDevices;
  networkErrorIndex: Number | undefined;
  userBackgroundFilterPreference: {
    blur: Boolean;
    image: Boolean;
  };
  currentBackgroundFilter: string;
  allowVirtualBackgrounds: Boolean;
  mirrorSelf: Boolean;
  showMuteNotifications: Boolean;
  didEstablishConnection: Boolean;
  noiseCancellationCompatible: Boolean;
  selectedNoiseSuppressionLevel: AIDenoiserProcessorLevel;
  noiseCancellationEnabled: Boolean;

  hdRecordingStatus: {
    user: HDRecordingStatus;
    screenshare: HDRecordingStatus;
  };

  sdRecordingStatus: {
    user: SDRecordingStatus;
    screenshare: SDRecordingStatus;
  };

  channelPeerConnectionStates: {
    [channelName: string]: {
      // This tell us the status of the peer connection, this is helpful in identifying if the user is connected to the channel
      peerConnectionState: RTCPeerConnectionState;
      // This tell us the status of the connection, this is helpful in identifying if the user is connected to the channel
      status: ConnectionState;
      // current progressive cloud proxy status [3,5], this is helpful in identifying if the user is using the progressive cloud proxy
      progressiveCloudProxyMode?: number | 'turn_server_fallback';
      // This is tells us the status of the progressive cloud proxy [NOT_STARTED, IN_PROGRESS, SUCCESS, EXHAUSTED]
      isProgressiveCloudProxyStatus?: ProgressiveCloudProxyStatus;
      // Current Proxy Mode Index, this is helpful in identifying next progressive cloud proxy mode
      currentProxyModeIndex?: number;
      // TURN Server acts as middleware between Agora and user clients, turnServerUrl represents what ports we will be using, turnServerFallbackStatus represents the connection state of the port
      turnServerFallbackStatus?: TurnServerFallbackStatus;
      turnServerUrl?: string;
    };
  };
}

export enum ProgressiveCloudProxyStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  SUCCESS = 'SUCCESS',
  EXHAUSTED = 'EXHAUSTED'
}

/**
 * TURN Server Fallback Status - Tracks the state of TURN server fallback when proxy is exhausted
 * TURN servers act as relay servers when direct peer-to-peer connection is not possible
 */
export enum TurnServerFallbackStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED'
}
